import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pricing, type PricingOverviewRow } from '@/lib/api';
import {
  Button, Card, EmptyState, Icon, Input, Skeleton, TableHead,
} from '@/design-system';
import { formatPriceNok, formatPercent } from '@/lib/format';
import { useToasts } from '@/lib/store';
import { BottleImage } from '@/components/BottleCard';
import { BottlePricingDrawer } from '@/components/BottlePricingDrawer';
import { UpgradeInlineNotice, isEntitlementError } from '@/components/UpgradeGate';

const ROW_COLUMNS = '28px minmax(160px,2fr) 100px 100px 110px 100px 80px 70px';

export function PrisautomatPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [markupInput, setMarkupInput] = useState('');
  const [detailBottleId, setDetailBottleId] = useState<string | undefined>(undefined);

  const overviewQ = useQuery({ queryKey: ['pricing.overview'], queryFn: () => Pricing.overview() });
  const rows = useMemo(() => overviewQ.data?.bottles ?? [], [overviewQ.data]);
  const tenantMarkup = overviewQ.data?.pricingMarkup;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pricing.overview'] });
    void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
  };

  const setPrice = useMutation({
    mutationFn: (input: { id: string; sellingPriceNok: number }) =>
      Pricing.bulk({ action: 'set-price', ids: [input.id], sellingPriceNok: input.sellingPriceNok }),
    onSuccess: invalidate,
    onError: (err: Error) => {
      if (isEntitlementError(err)) return;
      push({ title: t('common.error'), description: err.message, tone: 'danger' });
    },
  });

  const applySuggestedToSelected = useMutation({
    mutationFn: () => Pricing.bulk({ action: 'apply-suggested', ids: [...selectedIds] }),
    onSuccess: (data) => {
      push({
        title: t('pricing.bulk.updated', { count: data.updated.length }),
        description: data.skippedLocked.length
          ? t('pricing.bulk.skippedLocked', { count: data.skippedLocked.length })
          : undefined,
        tone: 'success',
      });
      setSelectedIds(new Set());
      invalidate();
    },
    onError: (err: Error) => {
      if (isEntitlementError(err)) return;
      push({ title: t('common.error'), description: err.message, tone: 'danger' });
    },
  });

  const applyMarkupToSelected = useMutation({
    mutationFn: (markup: number) => Pricing.bulk({ action: 'apply-markup', ids: [...selectedIds], markup }),
    onSuccess: (data) => {
      push({
        title: t('pricing.bulk.updated', { count: data.updated.length }),
        description: data.skippedLocked.length
          ? t('pricing.bulk.skippedLocked', { count: data.skippedLocked.length })
          : undefined,
        tone: 'success',
      });
      setSelectedIds(new Set());
      setMarkupInput('');
      invalidate();
    },
    onError: (err: Error) => {
      if (isEntitlementError(err)) return;
      push({ title: t('common.error'), description: err.message, tone: 'danger' });
    },
  });

  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.bottle.id)));
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">{t('pricing.title')}</h1>
          <p className="text-sm text-mist">{t('pricing.subtitle', { count: rows.length })}</p>
        </div>
        {tenantMarkup != null && (
          <span className="text-xs uppercase tracking-widest text-mist">Standard påslag: {tenantMarkup}×</span>
        )}
      </header>

      {(isEntitlementError(setPrice.error) || isEntitlementError(applySuggestedToSelected.error) || isEntitlementError(applyMarkupToSelected.error)) && (
        <UpgradeInlineNotice error={setPrice.error ?? applySuggestedToSelected.error ?? applyMarkupToSelected.error} />
      )}

      {selectedIds.size > 0 && (
        <Card className="flex flex-wrap items-center gap-3 p-3">
          <span className="text-sm text-parchment">{t('pricing.bulk.selected', { count: selectedIds.size })}</span>
          <Button
            size="sm"
            variant="primary"
            iconLeft="check"
            loading={applySuggestedToSelected.isPending}
            onClick={() => applySuggestedToSelected.mutate()}
          >
            {t('pricing.bulk.applySuggestedToSelected')}
          </Button>
          <div className="flex items-center gap-2">
            <Input
              placeholder={t('pricing.bulk.markupPlaceholder')}
              type="number"
              min={1}
              step={0.1}
              value={markupInput}
              onChange={(e) => setMarkupInput(e.target.value)}
              className="w-28"
            />
            <Button
              size="sm"
              variant="ghost"
              loading={applyMarkupToSelected.isPending}
              disabled={!markupInput || Number(markupInput) <= 0}
              onClick={() => applyMarkupToSelected.mutate(Number(markupInput))}
            >
              {t('pricing.bulk.applyMarkupToSelected')}
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelectedIds(new Set())}>
            {t('common.cancel')}
          </Button>
        </Card>
      )}

      {overviewQ.isLoading ? (
        <Skeleton height={320} rounded="md" />
      ) : rows.length === 0 ? (
        <EmptyState icon="receipt" title={t('pricing.empty')} />
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 760 }}>
            <TableHead
              gridTemplateColumns={ROW_COLUMNS}
              columns={[
                <input
                  key="all"
                  type="checkbox"
                  aria-label="Velg alle"
                  checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-[color:var(--ui-color-ruby)]"
                />,
                t('pricing.columns.name'),
                t('pricing.columns.purchase'),
                t('pricing.columns.market'),
                t('pricing.columns.selling'),
                t('pricing.columns.suggested'),
                t('pricing.columns.ratio'),
                t('pricing.columns.glass'),
              ]}
            />
            <div className="grid gap-1 py-1">
              {rows.map((row) => (
                <PriceRow
                  key={row.bottle.id}
                  row={row}
                  selected={selectedIds.has(row.bottle.id)}
                  onToggleSelect={() => toggleOne(row.bottle.id)}
                  onOpenDetail={() => setDetailBottleId(row.bottle.id)}
                  onSetPrice={(value) => setPrice.mutate({ id: row.bottle.id, sellingPriceNok: value })}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <BottlePricingDrawer
        open={Boolean(detailBottleId)}
        bottleId={detailBottleId}
        onClose={() => setDetailBottleId(undefined)}
      />
    </div>
  );
}

function PriceRow({
  row,
  selected,
  onToggleSelect,
  onOpenDetail,
  onSetPrice,
}: {
  row: PricingOverviewRow;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenDetail: () => void;
  onSetPrice: (value: number) => void;
}) {
  const { t } = useTranslation();
  const { bottle, catalogPriceNok, suggestedPriceNok, sellBuyRatioPct } = row;
  const [draftPrice, setDraftPrice] = useState<string>(bottle.sellingPriceNok != null ? String(bottle.sellingPriceNok) : '');

  const commit = () => {
    const value = draftPrice === '' ? undefined : Number(draftPrice);
    if (value != null && Number.isFinite(value) && value !== bottle.sellingPriceNok) {
      onSetPrice(value);
    }
  };

  const cancel = () => {
    setDraftPrice(bottle.sellingPriceNok != null ? String(bottle.sellingPriceNok) : '');
  };

  return (
    <div style={{ gridTemplateColumns: ROW_COLUMNS }} className="ui-sortable-row items-center py-2">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        aria-label={`Velg ${bottle.name}`}
        className="h-4 w-4 accent-[color:var(--ui-color-ruby)]"
      />
      <button type="button" onClick={onOpenDetail} className="flex min-w-0 items-center gap-3 text-left">
        <div className="relative grid h-14 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-ink-soft">
          <BottleImage src={bottle.photoUrl} iconSize={18} />
        </div>
        <span className="min-w-0">
          <span className="block truncate font-serif text-sm text-parchment">{bottle.name}</span>
          {bottle.lockSellingPrice && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-mist">
              <Icon name="lock" size={10} /> Låst
            </span>
          )}
        </span>
      </button>
      <span className="text-sm text-mist">{formatPriceNok(bottle.purchasePriceNok)}</span>
      <span className="text-sm text-mist">{formatPriceNok(catalogPriceNok)}</span>
      <Input
        aria-label={t('pricing.columns.selling')}
        type="number"
        min={0}
        step={5}
        value={draftPrice}
        onChange={(e) => setDraftPrice(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          // Enter commits immediately (don't wait for the row to lose
          // focus — a fast-typing owner shouldn't have to tab away just to
          // see the save happen), Escape reverts to the last saved value
          // instead of leaving a half-typed number that would otherwise
          // silently commit on the next blur.
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <span className="flex items-center gap-1 text-sm">
        <span className="text-parchment">{formatPriceNok(suggestedPriceNok)}</span>
        {suggestedPriceNok != null && (
          <button
            type="button"
            title={t('pricing.applySuggested')}
            onClick={() => {
              setDraftPrice(String(suggestedPriceNok));
              onSetPrice(suggestedPriceNok);
            }}
            className="text-mist hover:text-candle"
          >
            <Icon name="check" size={14} />
          </button>
        )}
      </span>
      <span className="text-sm text-mist">{formatPercent(sellBuyRatioPct)}</span>
      <span className="text-sm text-mist">{bottle.availableByGlass ? formatPriceNok(bottle.glassPriceNok) : '—'}</span>
    </div>
  );
}
